import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import type { AddressSearchResponseValue } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import {
  addressToAddressSearchValue,
  parseCartSnapshotCustomerAddress,
} from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import type { EnumDiscountUnit } from "gql/generated";

/** Discount applied to an invoice/renewal in the Invoice Admin panel — kept in
 * this shared context (rather than local state) so other parts of the modal
 * (e.g. the `ContentHeader` price tag) can reflect it too. */
export type InvoiceAdminAppliedDiscount = {
  discountId: string;
  name: string;
  promoCode?: string;
  unit: EnumDiscountUnit;
  /** Fraction (percentage) or cents (fixed) — same convention as the source Discount record. */
  amount: number;
  /**
   * When set, the invoice pricing engine uses this as the dollar discount
   * (cents) instead of re-deriving from `amount`. Needed for per-ticket
   * group discounts where the cart already computed rate × tickets.
   */
  computedAmount?: number;
  /** Whether this discount should apply to every future renewal (renewal-type products only). */
  isOngoing: boolean;
};

export type InvoiceRecipientContact = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: AddressSearchResponseValue | null;
};

export const EMPTY_INVOICE_RECIPIENT_CONTACT: InvoiceRecipientContact = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

export type PaymentRequestBillingHydration = {
  recipient?: string | null;
  recipientContact?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    address?: {
      line1?: string | null;
      line2?: string | null;
      city?: string | null;
      subdivision?: string | null;
      postalCode?: string | null;
      country?: string | null;
      location?: {
        type?: string | null;
        coordinates?: Array<number | null> | null;
      } | null;
    } | null;
  } | null;
  /** Cart snapshot often holds payer email even when `recipientContact` was never denormalized. */
  cartSnapshot?: {
    customer?: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
    customerAddress?: unknown;
  } | null;
  dueDate?: string | null;
  scheduledSendDate?: string | null;
};

export type InvoiceAdminBillingContextValue = {
  recipientContact: InvoiceRecipientContact;
  setRecipientContact: Dispatch<SetStateAction<InvoiceRecipientContact>>;
  recipientPersonId: string | null;
  setRecipientPersonId: Dispatch<SetStateAction<string | null>>;
  recipientSearch: string;
  setRecipientSearch: Dispatch<SetStateAction<string>>;
  billingContactsOnly: boolean;
  setBillingContactsOnly: Dispatch<SetStateAction<boolean>>;
  showBillingFields: boolean;
  setShowBillingFields: Dispatch<SetStateAction<boolean>>;
  dueDate: string;
  setDueDate: Dispatch<SetStateAction<string>>;
  scheduledSendDate: string;
  setScheduledSendDate: Dispatch<SetStateAction<string>>;
  billingReadOnly: boolean;
  setBillingReadOnly: Dispatch<SetStateAction<boolean>>;
  hydrateFromPaymentRequest: (pr: PaymentRequestBillingHydration) => void;
  appliedDiscount: InvoiceAdminAppliedDiscount | null;
  setAppliedDiscount: Dispatch<SetStateAction<InvoiceAdminAppliedDiscount | null>>;
};

const InvoiceAdminBillingContext =
  createContext<InvoiceAdminBillingContextValue | null>(null);

export function InvoiceAdminBillingProvider({ children }: { children: ReactNode }) {
  const [recipientContact, setRecipientContact] = useState<InvoiceRecipientContact>(
    EMPTY_INVOICE_RECIPIENT_CONTACT,
  );
  const [recipientPersonId, setRecipientPersonId] = useState<string | null>(null);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [billingContactsOnly, setBillingContactsOnly] = useState(false);
  const [showBillingFields, setShowBillingFields] = useState(false);
  const [dueDate, setDueDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [scheduledSendDate, setScheduledSendDate] = useState("");
  const [billingReadOnly, setBillingReadOnly] = useState(false);
  const [appliedDiscount, setAppliedDiscount] =
    useState<InvoiceAdminAppliedDiscount | null>(null);

  const hydrateFromPaymentRequest = useCallback(
    (pr: PaymentRequestBillingHydration) => {
      setRecipientPersonId(pr.recipient ? String(pr.recipient) : null);
      const cartCustomer = pr.cartSnapshot?.customer;
      const contact: InvoiceRecipientContact = {
        firstName:
          pr.recipientContact?.firstName?.trim() ||
          cartCustomer?.firstName?.trim() ||
          "",
        lastName:
          pr.recipientContact?.lastName?.trim() ||
          cartCustomer?.lastName?.trim() ||
          "",
        email:
          pr.recipientContact?.email?.trim() ||
          cartCustomer?.email?.trim() ||
          "",
        phone:
          pr.recipientContact?.phone?.trim() ||
          cartCustomer?.phone?.trim() ||
          "",
        address:
          parseCartSnapshotCustomerAddress(pr.cartSnapshot?.customerAddress) ??
          addressToAddressSearchValue(pr.recipientContact?.address) ??
          null,
      };
      setRecipientContact(contact);
      setShowBillingFields(Boolean(contact.firstName || contact.email));
      setDueDate(pr.dueDate?.slice(0, 10) ?? "");
      setScheduledSendDate(pr.scheduledSendDate?.slice(0, 10) ?? "");
    },
    [],
  );

  const value = useMemo(
    () => ({
      recipientContact,
      setRecipientContact,
      recipientPersonId,
      setRecipientPersonId,
      recipientSearch,
      setRecipientSearch,
      billingContactsOnly,
      setBillingContactsOnly,
      showBillingFields,
      setShowBillingFields,
      dueDate,
      setDueDate,
      scheduledSendDate,
      setScheduledSendDate,
      billingReadOnly,
      setBillingReadOnly,
      hydrateFromPaymentRequest,
      appliedDiscount,
      setAppliedDiscount,
    }),
    [
      recipientContact,
      recipientPersonId,
      recipientSearch,
      billingContactsOnly,
      showBillingFields,
      dueDate,
      scheduledSendDate,
      billingReadOnly,
      hydrateFromPaymentRequest,
      appliedDiscount,
    ],
  );

  return (
    <InvoiceAdminBillingContext.Provider value={value}>
      {children}
    </InvoiceAdminBillingContext.Provider>
  );
}

export function useInvoiceAdminBilling() {
  const ctx = useContext(InvoiceAdminBillingContext);
  if (!ctx) {
    throw new Error(
      "useInvoiceAdminBilling must be used within InvoiceAdminBillingProvider",
    );
  }
  return ctx;
}
